/**
 * Error handling utilities for job fetching
 * 
 * Provides consistent error handling across all contexts
 * (frontend public, frontend authenticated, backend)
 */

/**
 * Error types for job fetching operations
 */
export enum JobFetchErrorType {
  VALIDATION_ERROR = "VALIDATION_ERROR",
  EXTERNAL_API_ERROR = "EXTERNAL_API_ERROR",
  DATABASE_ERROR = "DATABASE_ERROR",
  NETWORK_ERROR = "NETWORK_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Structured error for job fetching operations
 */
export class JobFetchError extends Error {
  constructor(
    message: string,
    public readonly type: JobFetchErrorType,
    public readonly originalError?: unknown,
    public readonly context?: Record<string, unknown>
  ) {
    super(message);
    this.name = "JobFetchError";
  }
}

/**
 * Empty jobs response for graceful degradation
 */
export const EMPTY_JOBS_RESPONSE = {
  items: [],
  total: 0,
  limit: 0,
  offset: 0,
  has_more: false,
};

/**
 * Wrap a job fetching operation with consistent error handling
 * 
 * @param operation - Async operation to execute
 * @param context - Context information for logging
 * @param logger - Optional logger function
 * @returns Result or empty response on error
 * 
 * @example
 * ```typescript
 * const jobs = await withJobFetchErrorHandling(
 *   () => fetchJobsFromAPI(params),
 *   { creatorId: "123", context: "public_profile" },
 *   console.error
 * );
 * ```
 */
export async function withJobFetchErrorHandling<T>(
  operation: () => Promise<T>,
  context: Record<string, unknown>,
  logger?: (message: string, data: Record<string, unknown>) => void
): Promise<T | typeof EMPTY_JOBS_RESPONSE> {
  try {
    return await operation();
  } catch (error) {
    const errorType = categorizeError(error);
    const errorMessage = getErrorMessage(error);

    // Log error with context
    if (logger) {
      logger("Job fetching failed", {
        error: errorMessage,
        errorType,
        context,
        stack: error instanceof Error ? error.stack : undefined,
      });
    }

    // Return empty response for graceful degradation
    return EMPTY_JOBS_RESPONSE as any;
  }
}

/**
 * Categorize error by type
 */
function categorizeError(error: unknown): JobFetchErrorType {
  if (error instanceof JobFetchError) {
    return error.type;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes("validation") || message.includes("invalid")) {
      return JobFetchErrorType.VALIDATION_ERROR;
    }

    if (message.includes("api") || message.includes("external")) {
      return JobFetchErrorType.EXTERNAL_API_ERROR;
    }

    if (message.includes("database") || message.includes("not found")) {
      return JobFetchErrorType.DATABASE_ERROR;
    }

    if (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("fetch")
    ) {
      return JobFetchErrorType.NETWORK_ERROR;
    }
  }

  return JobFetchErrorType.UNKNOWN_ERROR;
}

/**
 * Extract error message from various error types
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (error && typeof error === "object" && "message" in error) {
    return String(error.message);
  }

  return "Unknown error occurred";
}

/**
 * Validate job parameters before making API call
 * Throws JobFetchError if validation fails
 */
export function validateJobParams(params: Record<string, unknown>): void {
  // Validate salary range
  if (
    params.salary_min !== undefined &&
    params.salary_max !== undefined &&
    typeof params.salary_min === "number" &&
    typeof params.salary_max === "number"
  ) {
    if (params.salary_min < 0) {
      throw new JobFetchError(
        "salary_min cannot be negative",
        JobFetchErrorType.VALIDATION_ERROR,
        undefined,
        { salary_min: params.salary_min }
      );
    }

    if (params.salary_max < 0) {
      throw new JobFetchError(
        "salary_max cannot be negative",
        JobFetchErrorType.VALIDATION_ERROR,
        undefined,
        { salary_max: params.salary_max }
      );
    }

    if (params.salary_min > params.salary_max) {
      throw new JobFetchError(
        "salary_min cannot be greater than salary_max",
        JobFetchErrorType.VALIDATION_ERROR,
        undefined,
        { salary_min: params.salary_min, salary_max: params.salary_max }
      );
    }
  }

  // Validate limit
  if (params.limit !== undefined) {
    const limit = Number(params.limit);
    if (isNaN(limit) || limit < 1 || limit > 100) {
      throw new JobFetchError(
        "limit must be between 1 and 100",
        JobFetchErrorType.VALIDATION_ERROR,
        undefined,
        { limit: params.limit }
      );
    }
  }

  // Validate offset
  if (params.offset !== undefined) {
    const offset = Number(params.offset);
    if (isNaN(offset) || offset < 0) {
      throw new JobFetchError(
        "offset cannot be negative",
        JobFetchErrorType.VALIDATION_ERROR,
        undefined,
        { offset: params.offset }
      );
    }
  }
}
